import React from "react";
import { motion } from "framer-motion";
import { Gem } from "lucide-react";

const T = { duration: 1.6, ease: [0.7, 0, 0.3, 1] };

function TuxCover({ d, opened, children }) {
  const lapel = (side) => ({ background: `linear-gradient(${side === "l" ? 100 : 260}deg, #050505, #1c1c1c 60%, #0b0b0b)`, clipPath: side === "l" ? "polygon(0 0,100% 0,100% 28%,62% 100%,0 100%)" : "polygon(0 0,100% 0,100% 100%,38% 100%,0 28%)" });
  return (
    <div className="absolute inset-0 z-10" style={{ perspective: 1400 }} data-testid="cover-tux">
      <motion.div className="absolute inset-y-0 left-1/2 w-[34%] -translate-x-1/2 bg-[#f7f4ee] flex flex-col items-center pt-[42%] gap-6" animate={opened ? { opacity: 0, y: 40 } : {}} transition={{ duration: 0.8, delay: 0.6 }}>
        {[0, 1, 2].map((i) => <span key={i} className="w-2.5 h-2.5 rounded-full bg-[#111]" />)}
      </motion.div>
      <motion.div className="absolute top-[18%] left-1/2 -translate-x-1/2 z-10 flex items-center" animate={opened ? { y: -80, opacity: 0, rotate: -20 } : {}} transition={{ duration: 0.7 }}>
        <span className="w-8 h-6 bg-[#111]" style={{ clipPath: "polygon(0 0,100% 50%,0 100%)" }} /><span className="w-3 h-4 rounded-sm" style={{ background: d.pal.accent }} /><span className="w-8 h-6 bg-[#111]" style={{ clipPath: "polygon(100% 0,0 50%,100% 100%)" }} />
      </motion.div>
      <motion.div className="absolute inset-y-0 left-0 w-1/2" style={{ ...lapel("l"), transformOrigin: "left" }} animate={opened ? { rotateY: -110, opacity: 0.1 } : {}} transition={{ ...T, delay: 0.3 }} />
      <motion.div className="absolute inset-y-0 right-0 w-1/2" style={{ ...lapel("r"), transformOrigin: "right" }} animate={opened ? { rotateY: 110, opacity: 0.1 } : {}} transition={{ ...T, delay: 0.3 }} />
      <span className="absolute left-[22%] top-[48%] w-8 h-5 rounded-t-full border-t-2 border-x-2 border-white/80 bg-white/90" style={{ opacity: opened ? 0 : 1 }} />
      {!opened && children}
    </div>
  );
}

function GownCover({ d, opened, children }) {
  return (
    <div className="absolute inset-0 z-10 overflow-hidden" data-testid="cover-gown">
      <motion.div className="absolute inset-0" style={{ background: `linear-gradient(180deg, #fffdf8, #f3ece0)` }} animate={opened ? { opacity: 0 } : {}} transition={{ duration: 1, delay: 1.2 }} />
      <motion.div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[85%] h-[62%]" style={{ background: "linear-gradient(180deg,#ffffff,#efe6d6)", clipPath: "polygon(38% 0,62% 0,66% 18%,100% 100%,0 100%,34% 18%)", boxShadow: "inset 0 0 40px #d9cbb3" }}
        animate={opened ? { y: "100%" } : {}} transition={{ ...T, delay: 0.5 }} />
      <motion.div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 20%, #fff8 2px, transparent 3px) 0 0/22px 22px, linear-gradient(180deg,#ffffffd9,#fffaf0b3)", backdropFilter: "blur(3px)", transformOrigin: "top" }}
        animate={opened ? { y: "-105%", scaleX: 0.7, opacity: 0.4 } : { y: 0 }} transition={T} />
      <span className="absolute inset-x-0 top-0 h-1" style={{ background: d.pal.accent, opacity: opened ? 0 : 1 }} />
      {!opened && children}
    </div>
  );
}

function RingBoxCover({ d, opened, children }) {
  return (
    <motion.div className="absolute inset-0 z-10" style={{ background: `radial-gradient(circle, ${d.pal.panel}, ${d.pal.bg})`, perspective: 1000 }} animate={opened ? { opacity: 0 } : {}} transition={{ duration: 0.9, delay: 1.5 }} data-testid="cover-ringbox">
      {opened && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-40 h-28">
            <motion.div className="absolute inset-x-0 top-0 h-1/2 rounded-t-2xl" style={{ background: "linear-gradient(180deg,#5a0f1f,#33060f)", transformOrigin: "bottom" }} initial={{ rotateX: 0 }} animate={{ rotateX: -120 }} transition={{ duration: 0.8 }} />
            <div className="absolute inset-x-0 bottom-0 h-1/2 rounded-b-2xl flex items-start justify-center" style={{ background: "linear-gradient(180deg,#33060f,#1c0308)" }}>
              <motion.span initial={{ scale: 0, y: 10 }} animate={{ scale: 1.2, y: -18 }} transition={{ delay: 0.6, type: "spring" }} style={{ color: d.pal.accent, filter: `drop-shadow(0 0 14px ${d.pal.accent})` }}><Gem className="w-10 h-10" /></motion.span>
            </div>
          </div>
        </div>
      )}
      {!opened && children}
    </motion.div>
  );
}

function ChampagneCover({ d, opened, children }) {
  return (
    <motion.div className="absolute inset-0 z-10 overflow-hidden" style={{ background: `linear-gradient(180deg, ${d.pal.bg}, ${d.pal.panel})` }} animate={opened ? { opacity: 0 } : {}} transition={{ duration: 1, delay: 1.6 }} data-testid="cover-champagne">
      {opened && <>
        <motion.span className="absolute left-1/2 bottom-1/3 w-4 h-5 rounded-sm" style={{ background: "#c79a5a" }} initial={{ y: 0, rotate: 0 }} animate={{ y: -600, rotate: 540 }} transition={{ duration: 1 }} />
        {Array.from({ length: 24 }, (_, i) => (
          <motion.span key={i} className="absolute bottom-1/3 rounded-full border" style={{ left: `${40 + ((i * 7) % 20)}%`, width: 6 + (i % 4) * 3, height: 6 + (i % 4) * 3, borderColor: d.pal.accent, background: `${d.pal.accent}33` }}
            initial={{ y: 0, opacity: 1 }} animate={{ y: -500 - (i % 5) * 60, x: ((i % 7) - 3) * 30, opacity: 0 }} transition={{ duration: 1.6, delay: i * 0.03 }} />
        ))}
      </>}
      {!opened && children}
    </motion.div>
  );
}

export const WEDDING_COVERS = { tux: TuxCover, gown: GownCover, ringbox: RingBoxCover, champagne: ChampagneCover };
